<template>
  <div class="site-imports">
    <!-- Header -->
    <div class="site-imports__header">
      <h3 class="site-imports__title">Import Management</h3>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="site-imports__alert site-imports__alert--error" role="alert">
      <p>{{ error }}</p>
    </div>

    <!-- Success Message -->
    <div v-if="success" class="site-imports__alert site-imports__alert--success" role="alert">
      <p>{{ success }}</p>
    </div>

    <!-- Select Type Tabs and Upload Button -->
    <div v-if="canImport()" class="site-imports__controls">
      <div class="site-imports__type-selector">
        <span class="site-imports__type-label">Select Type:</span>
        <div class="site-imports__type-tabs">
          <button
            @click="handleTypeChange('customers')"
            class="site-imports__type-tab"
            :class="{ 'site-imports__type-tab--active': importType === 'customers' }"
            :aria-label="'Select customers import type'"
          >
            <theme-icon icon="users-icon" type="icons" classes="site-imports__type-icon" />
            <span>Customers</span>
          </button>
          <button
            @click="handleTypeChange('employees')"
            class="site-imports__type-tab"
            :class="{ 'site-imports__type-tab--active': importType === 'employees' }"
            :aria-label="'Select employees import type'"
          >
            <theme-icon icon="briefcase-icon" type="icons" classes="site-imports__type-icon" />
            <span>Employees</span>
          </button>
          <button
            @click="handleTypeChange('competitors')"
            class="site-imports__type-tab"
            :class="{ 'site-imports__type-tab--active': importType === 'competitors' }"
            :aria-label="'Select competitors import type'"
          >
            <theme-icon icon="building-icon" type="icons" classes="site-imports__type-icon" />
            <span>Competitors</span>
          </button>
        </div>
      </div>
      
      <div class="site-imports__actions">
        <a
          v-if="importType"
          href="#"
          @click.prevent="downloadSampleCsv"
          class="site-imports__sample-link"
          :aria-label="`Download sample file for ${importType}`"
        >
          <theme-icon icon="download-icon" type="icons" classes="site-imports__sample-icon" />
          <span>Sample File</span>
        </a>
        
        <themed-button
          @click="$refs.fileInput.click()" 
          :disabled="!importType || uploading"
          :aria-label="uploading ? 'Uploading file' : 'Upload file'"
          variant="default"
          size="default"
        >
          <theme-icon 
            v-if="uploading"
            icon="spinner-icon" 
            type="icons" 
            classes="site-imports__spinner" 
          />
          <theme-icon 
            v-else
            icon="upload-icon" 
            type="icons" 
          />
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </themed-button>
        
        <input 
          ref="fileInput"
          type="file" 
          accept=".csv" 
          class="site-imports__file-input" 
          @change="handleFileUpload"
          aria-label="Select CSV file to upload"
        >
      </div>
    </div>

    <!-- Sub Navigation Tabs -->
    <div class="site-imports__tabs">
      <nav class="site-imports__tabs-nav" role="tablist" aria-label="Import data sections">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="site-imports__tab"
          :class="{ 'site-imports__tab--active': activeTab === tab.id }"
          role="tab"
          :aria-selected="activeTab === tab.id"
          :aria-controls="`tab-panel-${tab.id}`"
          :id="`tab-${tab.id}`"
        >
          {{ tab.name }}
        </button>
      </nav>
    </div>
    
    <!-- Table Content -->
    <div 
      class="site-imports__content"
      role="tabpanel"
      :aria-labelledby="`tab-${activeTab}`"
      :id="`tab-panel-${activeTab}`"
    >
      <themed-table
        :key="activeTab"
        :api-endpoint="currentApiEndpoint"
        :columns="tableColumns"
        :pagination="true"
        aria-label="Import data table"
        hoverable
      />
    </div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';

export default {
  name: 'SiteImports',
  
  components: {
    ThemedButton,
    ThemedTable
  },
  
  props: {
    siteId: {
      type: [String, Number],
      required: true
    },
    initialCustomers: {
      type: Array,
      default: () => []
    }
  },
  
  data() {
    return {
      activeTab: 'imports',
      importType: '',
      uploading: false,
      error: null,
      success: null,
      importTypeOptions: [
        { label: 'Customers', value: 'customers' },
        { label: 'Employees', value: 'employees' },
        { label: 'Competitors', value: 'competitors' }
      ]
    };
  },
  
  computed: {
    currentApiEndpoint() {
      const endpointMap = {
        'imports': `/sites/${this.siteId}/imports`,
        'customers': `/sites/${this.siteId}/customers`,
        'employees': `/sites/${this.siteId}/employees`,
        'competitors': `/sites/${this.siteId}/competitors`
      };
      
      return endpointMap[this.activeTab] || `/sites/${this.siteId}/${this.activeTab}`;
    },
    
    tabs() {
      return [
        { id: 'imports', name: 'Import Logs', active: true, count: 0 },
        { id: 'customers', name: 'Customers', count: 0 },
        { id: 'employees', name: 'Employees', count: 0 },
        { id: 'competitors', name: 'Competitors', count: 0 },
      ];
    },
    
    tableColumns() {
      const importLogsColumns = [
        { 
          field: 'created_at', 
          label: 'Date',
          formatter: (value) => this.formatDate(value)
        },
        { 
          field: 'type', 
          label: 'Type',
          formatter: (value) => value ? value.charAt(0).toUpperCase() + value.slice(1) : 'N/A'
        },
        { field: 'file_name', label: 'File Name' },
        { 
          field: 'imported_by', 
          label: 'Importer',
          formatter: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
        },
        { 
          field: 'status', 
          label: 'Status',
          formatter: (value, row) => {
            const variant = this.getStatusBadgeVariant(value);
            const statusText = value || 'N/A';
            const variantClasses = {
              'warning': 'themed-badge--warning',
              'info': 'themed-badge--info',
              'success': 'themed-badge--success',
              'destructive': 'themed-badge--destructive',
              'secondary': 'themed-badge--secondary'
            };
            const badgeClass = variantClasses[variant] || 'themed-badge--secondary';
            return `<span class="themed-badge ${badgeClass}">${statusText}</span>`;
          }
        },
        { 
          field: 'imported_count', 
          label: '# Imported',
          align: 'right'
        },
        { 
          field: 'failed_count', 
          label: 'Failed',
          align: 'right'
        },
        {
          field: 'actions',
          label: 'Actions',
          type: 'actions',
          formatter: (value, row) => {
            if (row.failed_count > 0) {
              return `<a href="/sites/${this.siteId}/import/${row.id}/errors" class="site-imports__action-link" title="Download Error Log">
                <svg class="w-4 h-4 mr-1" style="display: inline-block; vertical-align: middle; width: 1rem; height: 1rem; margin-right: 0.25rem;" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/>
                </svg>
                Errors
              </a>`;
            }
            return '-';
          }
        }
      ];

      const customerColumns = [
        { field: 'name', label: 'Company' },
        { field: 'address', label: 'Address' },
        { field: 'city', label: 'City' },
        { field: 'state', label: 'ST' },
        { field: 'postal_code', label: 'ZIP' },
        { 
          field: 'country', 
          label: 'CTRY',
          formatter: (value) => {
            if (!value) return 'N/A';
            return value.name || (typeof value === 'string' ? value : 'N/A');
          }
        },
        { 
          field: 'contact', 
          label: 'Contact',
          formatter: (value) => {
            // Return a contact icon or button placeholder
            return value ? '👤' : '-';
          }
        },
        { 
          field: 'created_at', 
          label: 'Date',
          formatter: (value) => this.formatDate(value)
        },
        { 
          field: 'imported_by', 
          label: 'Importer',
          formatter: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
        }
      ];

      const employeeColumns = [
        { field: 'employee_number', label: '#', align: 'right' },
        { field: 'job_function', label: 'Function' },
        { field: 'solution', label: 'Subsidiary' },
        { field: 'address', label: 'Address' },
        { field: 'city', label: 'City' },
        { field: 'state', label: 'ST' },
        { field: 'postal_code', label: 'ZIP' },
        { 
          field: 'created_at', 
          label: 'Date',
          formatter: (value) => this.formatDate(value)
        },
        { 
          field: 'imported_by', 
          label: 'Importer',
          formatter: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
        }
      ];

      const competitorColumns = [
        { field: 'name', label: 'Company' },
        { field: 'region', label: 'Region' },
        { field: 'address', label: 'Address' },
        { field: 'city', label: 'City' },
        { field: 'state', label: 'ST' },
        { field: 'postal_code', label: 'ZIP' },
        { 
          field: 'country', 
          label: 'CTRY',
          formatter: (value) => {
            if (!value) return 'N/A';
            return value.name || (typeof value === 'string' ? value : 'N/A');
          }
        },
        { 
          field: 'created_at', 
          label: 'Date',
          formatter: (value) => this.formatDate(value)
        },
        { 
          field: 'imported_by', 
          label: 'Importer',
          formatter: (value) => value ? (value.name || value.email || 'N/A') : 'N/A'
        }
      ];

      switch (this.activeTab) {
        case 'employees':
          return employeeColumns;
        case 'competitors':
          return competitorColumns;
        case 'customers':
          return customerColumns;
        case 'imports':
        default:
          return importLogsColumns;
      }
    }
  },

  methods: {
    canImport() {
      return this.$gate.can('update', 'site', this.siteId) || this.$gate.can('create', 'site');
    },
    
    handleTypeChange(type) {
      this.importType = type;
      this.activeTab = type;
    },
    
    refreshCurrentTable() {
      // Refresh the current table after import
      if (this.$refs.importsTable) {
        this.$refs.importsTable.refresh();
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.uploading = true;
      this.error = null;
      this.success = null;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', this.importType);
      formData.append('_method', 'POST');

      const endpoint = `/sites/${this.siteId}/import/${this.importType}`;
        
      axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        }
      })
      .then(response => {
        this.success = response.data.message || 'Import has been queued. Please wait...';
        this.refreshCurrentTable();
        
        const importLogId = response.data.import_log_id;
        this.pollImportStatus(importLogId);
      })
      .catch(error => {
        this.error = error.response?.data?.message || 'An error occurred while uploading the file.';
        this.uploading = false;
      })
      .finally(() => {
        event.target.value = null;
      });
    },

    pollImportStatus(importLogId, attempt = 1) {
      const maxAttempts = 60;
      
      if (attempt > maxAttempts) {
        this.error = 'Import is taking longer than expected. Please check back later.';
        this.uploading = false;
        return;
      }
      
      axios.get(`/sites/${this.siteId}/import/${importLogId}/status`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        const { status, imported_count, failed_count, total_rows, errors } = response.data;
        
        if (status === 'completed') {
          this.success = `Successfully imported ${imported_count} ${this.importType}.`;
          if (failed_count > 0) {
            this.error = `Failed to import ${failed_count} ${this.importType}.`;
          }
          this.uploading = false;
          this.refreshCurrentTable();
          this.$emit('imported');
        } 
        else if (status === 'failed') {
          this.error = 'Import failed. ' + (errors?.[0]?.message || 'Please check the file and try again.');
          this.uploading = false;
        }
        else {
          setTimeout(() => {
            this.pollImportStatus(importLogId, attempt + 1);
          }, 5000);
          
          this.success = `Processing... (${imported_count + failed_count}/${total_rows || '?'} records processed)`;
        }
      })
      .catch(error => {
        if (attempt < maxAttempts) {
          setTimeout(() => {
            this.pollImportStatus(importLogId, attempt + 1);
          }, 5000);
        } else {
          this.error = 'Unable to check import status. Please refresh the page to check if the import completed.';
          this.uploading = false;
        }
      });
    },

    async downloadSampleCsv() {
      try {
        const importType = this.importType;
        this.uploading = false;
        
        const response = await axios({
          url: `/sample-${importType}-import`,
          method: 'GET',
          responseType: 'blob',
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `sample-${importType}-import.xlsx`);
        document.body.appendChild(link);
        link.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        this.error = 'Failed to download sample file. Please try again.';
      } finally {
        this.uploading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const options = { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    },

    getStatusBadgeVariant(status) {
      const statusMap = {
        'pending': 'warning',
        'processing': 'info',
        'completed': 'success',
        'failed': 'destructive'
      };
      return statusMap[status?.toLowerCase()] || 'secondary';
    }
  }
};
</script>
